import { useTranslation } from 'react-i18next';

import { RatingBar } from '@/components/cr/RatingBar';
import { overallRating, ratingLabelKey, ratingToneClass } from '@/lib/crDeckRating';
import type { DeckRating } from '@/types/qbot';

interface DeckStatsProps {
  archetype?: string;
  rating?: DeckRating;
  avgElixir?: number;
  /**
   * The rating is genuinely being fetched (current deck) → show a skeleton.
   * When false and there's no rating, the deck simply has no rating yet
   * (saved / meta decks, until qbot serves it) → show the pending note, not a
   * skeleton that would spin forever.
   */
  loading?: boolean;
}

/**
 * The verdict line + five axes, shared by every deck page.
 *
 * The aggregate (the "6.2 Good") is ours — the API returns only the five axes;
 * the label thresholds mirror qbot's getRatingLabel so both surfaces agree on
 * what "Great" means.
 */
export function DeckStats({ archetype, rating, avgElixir, loading }: DeckStatsProps) {
  const { t } = useTranslation('cr');

  const head = (
    <p className="flex items-baseline gap-2 text-xs">
      {archetype ? <span className="min-w-0 truncate font-bold text-fg">{archetype}</span> : null}
      {rating && (
        <>
          <span
            className={`shrink-0 font-bold uppercase tracking-wide ${ratingToneClass(overallRating(rating))}`}
          >
            {t(`panel.rating.${ratingLabelKey(overallRating(rating))}`)}
          </span>
          <span className="shrink-0 tabular-nums text-fg-muted">
            {overallRating(rating).toFixed(1)}
          </span>
        </>
      )}
      {typeof avgElixir === 'number' && (
        <span className="ml-auto shrink-0 tabular-nums text-fg-muted">
          ⚡ {avgElixir.toFixed(1)}
        </span>
      )}
    </p>
  );

  if (!rating) {
    return (
      <div className="flex h-full flex-col gap-1.5">
        {head}
        {loading ? (
          <div className="flex flex-col gap-1.5" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-3 animate-pulse rounded bg-bg-secondary" />
            ))}
          </div>
        ) : (
          <p className="text-[11px] leading-snug text-fg-muted">{t('panel.rating.pending')}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-1.5">
      {head}
      <RatingBar label={t('deckCheck.label.atk')} value={rating.attack} className="gap-2" />
      <RatingBar label={t('deckCheck.label.def')} value={rating.defense} className="gap-2" />
      <RatingBar label={t('deckCheck.label.syn')} value={rating.synergy} className="gap-2" />
      <RatingBar label={t('deckCheck.label.ver')} value={rating.versatility} className="gap-2" />
      <RatingBar label={t('deckCheck.label.bal')} value={rating.balance} className="gap-2" />
    </div>
  );
}
